import { useState } from "react";
import { Button, Card, Badge } from "@/components/ui";
import { X, Check, Zap, Crown, Star, ExternalLink, Loader2, Clock, Calendar, CalendarDays } from "lucide-react";
import { cn } from "@/lib/utils";
import { API_BASE } from "@/lib/api";

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  currentPlan?: string;
}

const ALL_PLANS = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    period: "forever",
    description: "Perfect to try things out",
    icon: Zap,
    popular: false,
    features: [
      "3 files per month",
      "Full summaries",
      "Full MCQs",
      "Full flashcards",
      "15 Lerni Bot messages per month",
    ],
    cta: "Get Started Free",
    ctaVariant: "outline" as const,
  },
  {
    id: "weekly",
    name: "Weekly",
    price: "$1.99",
    period: "/week",
    description: "Great for exam crunch time",
    icon: Clock,
    popular: false,
    features: [
      "Unlimited files",
      "Unlimited summaries",
      "Unlimited MCQs",
      "Unlimited flashcards",
      "Lerni Bot included",
      "Faster generation",
    ],
    cta: "Start Weekly",
    ctaVariant: "outline" as const,
  },
  {
    id: "monthly",
    name: "Monthly",
    price: "$4.99",
    period: "/month",
    description: "Best for consistent study",
    icon: Crown,
    popular: true,
    features: [
      "Everything in Weekly",
      "Priority generation",
      "Most Popular",
    ],
    cta: "Start Monthly",
    ctaVariant: "default" as const,
  },
  {
    id: "yearly",
    name: "Yearly",
    price: "$49",
    period: "/year",
    description: "Best value for serious students",
    icon: Star,
    popular: false,
    features: [
      "Everything in Monthly",
      "Best value",
      "Early access to new features",
    ],
    cta: "Go Yearly",
    ctaVariant: "outline" as const,
  },
];

export function UpgradeModal({ open, onClose, currentPlan = "free" }: UpgradeModalProps) {
  const [isRedirecting, setIsRedirecting] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  if (!open) return null;

  async function handleCheckout(planId: string) {
    if (planId === "free" || planId === currentPlan || isRedirecting) return;

    setIsRedirecting(planId);
    setCheckoutError(null);

    try {
      const token = localStorage.getItem("studyflow_token");
      const res = await fetch(`${API_BASE}/api/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ plan: planId }),
      });

      const data = await res.json();

      if (!res.ok) {
        setCheckoutError(data?.message ?? "Failed to start checkout. Please try again.");
        setIsRedirecting(null);
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        setCheckoutError("No checkout URL returned. Please try again.");
        setIsRedirecting(null);
      }
    } catch {
      setCheckoutError("Network error. Please try again.");
      setIsRedirecting(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-5xl bg-card border border-border rounded-3xl shadow-2xl p-6 sm:p-8 animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-muted-foreground hover:text-foreground transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-4">
            <Zap className="w-4 h-4" />
            Simple Pricing
          </div>
          <h2 className="text-2xl font-display font-bold text-foreground">
            Start free. Upgrade when ready.
          </h2>
          <p className="text-muted-foreground mt-2">
            No hidden fees. Cancel anytime.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ALL_PLANS.map((plan) => {
            const isCurrent = plan.id === currentPlan;
            const isRedirectingThis = isRedirecting === plan.id;
            const isPaid = plan.id !== "free";
            const Icon = plan.icon;

            return (
              <Card
                key={plan.id}
                className={cn(
                  "relative p-5 flex flex-col transition-all",
                  plan.popular
                    ? "border-primary shadow-lg shadow-primary/10 ring-1 ring-primary/20"
                    : "",
                  isCurrent && "opacity-60"
                )}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground shadow-lg px-3 whitespace-nowrap">
                    Most Popular
                  </Badge>
                )}

                <div className="mb-4">
                  <div
                    className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center mb-3",
                      plan.popular ? "bg-primary/10" : "bg-muted"
                    )}
                  >
                    <Icon
                      className={cn(
                        "w-5 h-5",
                        plan.popular ? "text-primary" : "text-muted-foreground"
                      )}
                    />
                  </div>
                  <h3 className="text-lg font-display font-bold">{plan.name}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {plan.description}
                  </p>
                </div>

                <div className="mb-4">
                  <span className="text-3xl font-bold font-display">{plan.price}</span>
                  <span className="text-muted-foreground text-sm ml-1">
                    {plan.period}
                  </span>
                </div>

                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm">
                      <Check
                        className={cn(
                          "w-3.5 h-3.5 shrink-0 mt-0.5",
                          plan.popular ? "text-primary" : "text-muted-foreground"
                        )}
                      />
                      <span className="leading-tight">{f}</span>
                    </li>
                  ))}
                </ul>

                {isCurrent ? (
                  <Badge
                    variant="outline"
                    className="w-full justify-center py-2.5"
                  >
                    Current Plan
                  </Badge>
                ) : (
                  <Button
                    variant={plan.ctaVariant}
                    className={cn(
                      "w-full rounded-xl",
                      plan.popular &&
                        "bg-primary text-primary-foreground hover:bg-primary/90"
                    )}
                    disabled={!!isRedirecting || !isPaid}
                    onClick={() => isPaid && handleCheckout(plan.id)}
                  >
                    {isRedirectingThis ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Redirecting...
                      </>
                    ) : (
                      plan.cta
                    )}
                  </Button>
                )}
              </Card>
            );
          })}
        </div>

        {checkoutError && (
          <p className="text-center text-sm text-destructive mt-4">
            {checkoutError}
          </p>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
          <span>Cancel anytime</span>
          <span className="hidden sm:inline">|</span>
          <span>No hidden fees</span>
          <span className="hidden sm:inline">|</span>
          <span>Secure checkout via Polar</span>
        </div>
      </div>
    </div>
  );
}
